import { Link } from "@mui/material";
import { Fragment, ReactNode } from "react";

/**
 * Parses links in a string and converts them to React elements.
 * Supports both markdown-style links and HTML anchor tags, as well as mailto links.
 *
 * Example inputs:
 * - Markdown: "Use the [ADEM eFile system](https://example.com) to find your ID."
 * - HTML: 'Use <a href="https://example.com">this site</a> to find info.'
 *
 * Returns: JSX with clickable links
 */
export function parseTooltipLinks(text: string | undefined): ReactNode {
  if (!text) return undefined;

  // Combined regex to match both:
  // 1. Markdown links: [text](url)
  // 2. HTML anchor tags: <a href="url">text</a> or <a href="url" target="_blank" rel="...">text</a>
  const linkRegex =
    /\[([^\]]+)\]\(([^)]+)\)|<a\s+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/g;

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyIndex = 0;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(
        <Fragment key={keyIndex++}>{text.slice(lastIndex, match.index)}</Fragment>
      );
    }

    // Determine if it's a markdown link or HTML link
    const isMarkdown = match[1] !== undefined;
    const linkText = isMarkdown ? match[1] : match[4];
    const url = isMarkdown ? match[2] : match[3];

    // Add the link
    parts.push(
      <Link
        key={keyIndex++}
        href={url}
        target={url.startsWith("mailto:") ? undefined : "_blank"}
        rel={url.startsWith("mailto:") ? undefined : "noopener noreferrer"}
        onClick={(e) => e.stopPropagation()}
      >
        {linkText}
      </Link>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last link
  if (lastIndex < text.length) {
    parts.push(<Fragment key={keyIndex++}>{text.slice(lastIndex)}</Fragment>);
  }

  // If no links found, return original text
  if (parts.length === 0) {
    return text;
  }

  return <>{parts}</>;
}
